import { useState, useEffect, useCallback } from "react";
import { Calendar, Clock, Plus, X, Save, Trash2, Edit2, Mic2, RefreshCw, User } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const COLORS = [
  { bg: "bg-purple-500/20", border: "border-purple-500/40", text: "text-purple-300", dot: "bg-purple-500" },
  { bg: "bg-blue-500/20", border: "border-blue-500/40", text: "text-blue-300", dot: "bg-blue-500" },
  { bg: "bg-emerald-500/20", border: "border-emerald-500/40", text: "text-emerald-300", dot: "bg-emerald-500" },
  { bg: "bg-orange-500/20", border: "border-orange-500/40", text: "text-orange-300", dot: "bg-orange-500" },
  { bg: "bg-pink-500/20", border: "border-pink-500/40", text: "text-pink-300", dot: "bg-pink-500" },
  { bg: "bg-teal-500/20", border: "border-teal-500/40", text: "text-teal-300", dot: "bg-teal-500" },
  { bg: "bg-red-500/20", border: "border-red-500/40", text: "text-red-300", dot: "bg-red-500" },
];

const formatHour = (h, m) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const emptyForm = {
  show_name: "", dj_name: "", dj_id: "", day_of_week: 0,
  start_hour: 0, start_minute: 0, end_hour: 1, end_minute: 0,
  description: "", image_url: "", is_active: true,
};

export const ShowScheduleTab = ({ currentUser, authToken }) => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [djProfiles, setDjProfiles] = useState([]);

  const stationId = currentUser?.station_id || currentUser?.azuracast_station_id || currentUser?.whitelabel_slug;

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const fetchShows = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shows/${stationId}`);
      if (res.ok) {
        const data = await res.json();
        setShows(data.shows || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [stationId]);

  useEffect(() => { fetchShows(); }, [fetchShows]);

  // Fetch DJ profiles for the selector
  useEffect(() => {
    if (!stationId) return;
    fetch(`${BACKEND_URL}/api/dj-profiles/${stationId}`)
      .then(r => r.json())
      .then(d => setDjProfiles(d.profiles || []))
      .catch(() => {});
  }, [stationId]);

  const handleSave = async () => {
    if (!form.show_name.trim() || !form.dj_name.trim()) {
      flash("Show name and DJ name are required", "error");
      return;
    }
    setSaving(true);
    try {
      const url = editingId
        ? `${BACKEND_URL}/api/shows/${editingId}`
        : `${BACKEND_URL}/api/shows`;
      const method = editingId ? "PUT" : "POST";
      const body = editingId ? form : { ...form, station_id: stationId };
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        flash(editingId ? "Show updated" : "Show created");
        setShowForm(false);
        setEditingId(null);
        setForm({ ...emptyForm });
        fetchShows();
      } else {
        const d = await res.json();
        flash(d.detail || "Failed to save", "error");
      }
    } catch (e) { flash(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (showId) => {
    if (!window.confirm("Delete this show?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/shows/${showId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) { flash("Show deleted"); fetchShows(); }
      else flash("Failed to delete", "error");
    } catch (e) { flash(e.message, "error"); }
  };

  const startEdit = (show) => {
    setForm({
      show_name: show.show_name,
      dj_name: show.dj_name,
      dj_id: show.dj_id || "",
      day_of_week: show.day_of_week,
      start_hour: show.start_hour,
      start_minute: show.start_minute,
      end_hour: show.end_hour,
      end_minute: show.end_minute,
      description: show.description || "",
      image_url: show.image_url || "",
      is_active: show.is_active !== false,
    });
    setEditingId(show.show_id);
    setShowForm(true);
  };

  // Group shows by day for grid
  const showsByDay = {};
  DAYS.forEach((_, i) => { showsByDay[i] = []; });
  shows.forEach(s => { if (showsByDay[s.day_of_week]) showsByDay[s.day_of_week].push(s); });

  if (!stationId) {
    return (
      <div className="text-center py-16" data-testid="shows-no-station">
        <Calendar className="w-12 h-12 text-white/15 mx-auto mb-3" />
        <p className="text-white/40 text-sm">Set up your station first to manage show schedules.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="show-schedule-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-400" /> Show Schedule
          </h3>
          <p className="text-white/40 text-sm mt-1">
            {shows.length} show{shows.length !== 1 ? "s" : ""} scheduled — displayed on your station page "On Air Now" widget
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchShows} className="p-2.5 text-white/40 hover:text-white rounded-xl hover:bg-white/5 transition" data-testid="shows-refresh">
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm({ ...emptyForm }); }}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition"
            data-testid="shows-add-btn"
          >
            <Plus className="w-4 h-4" /> Add Show
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`px-4 py-2.5 rounded-xl text-sm font-medium ${
          message.type === "error" ? "bg-red-500/15 text-red-300 border border-red-500/20" : "bg-green-500/15 text-green-300 border border-green-500/20"
        }`}>{message.text}</div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white/[0.03] border border-purple-500/20 rounded-2xl p-6 space-y-5" data-testid="shows-form">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold">{editingId ? "Edit Show" : "New Show"}</h4>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-white/30 hover:text-white transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Show Name</label>
              <input
                value={form.show_name} onChange={e => setForm({ ...form, show_name: e.target.value })}
                placeholder="Morning Drive"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40"
                data-testid="shows-form-name"
              />
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">DJ / Host</label>
              {djProfiles.length > 0 ? (
                <select
                  value={form.dj_id}
                  onChange={e => {
                    const djId = e.target.value;
                    const dj = djProfiles.find(d => d.dj_id === djId);
                    setForm({ ...form, dj_id: djId, dj_name: dj ? dj.name : form.dj_name });
                  }}
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/40"
                  data-testid="shows-form-dj-select"
                >
                  <option value="">Select a DJ...</option>
                  {djProfiles.map(d => (
                    <option key={d.dj_id} value={d.dj_id}>{d.name} ({d.role || "DJ"})</option>
                  ))}
                </select>
              ) : (
                <input
                  value={form.dj_name} onChange={e => setForm({ ...form, dj_name: e.target.value })}
                  placeholder="DJ Mike"
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40"
                  data-testid="shows-form-dj"
                />
              )}
              {djProfiles.length > 0 && !form.dj_id && (
                <input
                  value={form.dj_name} onChange={e => setForm({ ...form, dj_name: e.target.value })}
                  placeholder="Or type a name manually"
                  className="w-full mt-2 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40"
                  data-testid="shows-form-dj-manual"
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Day of Week</label>
              <select
                value={form.day_of_week} onChange={e => setForm({ ...form, day_of_week: parseInt(e.target.value) })}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/40"
                data-testid="shows-form-day"
              >
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Start Time</label>
              <div className="flex gap-2">
                <select value={form.start_hour} onChange={e => setForm({ ...form, start_hour: parseInt(e.target.value) })}
                  className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/40"
                  data-testid="shows-form-start-hour"
                >
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}</option>)}
                </select>
                <select value={form.start_minute} onChange={e => setForm({ ...form, start_minute: parseInt(e.target.value) })}
                  className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/40"
                  data-testid="shows-form-start-min"
                >
                  {[0, 15, 30, 45].map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">End Time</label>
              <div className="flex gap-2">
                <select value={form.end_hour} onChange={e => setForm({ ...form, end_hour: parseInt(e.target.value) })}
                  className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/40"
                  data-testid="shows-form-end-hour"
                >
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}</option>)}
                </select>
                <select value={form.end_minute} onChange={e => setForm({ ...form, end_minute: parseInt(e.target.value) })}
                  className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/40"
                  data-testid="shows-form-end-min"
                >
                  {[0, 15, 30, 45].map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-white/50 text-xs mb-1.5 uppercase tracking-wider">Description (optional)</label>
            <input
              value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="The best morning jams to wake up to"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40"
              data-testid="shows-form-desc"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-white/20 bg-black/30 text-purple-500 focus:ring-purple-500" />
              <span className="text-white/60 text-sm">Active</span>
            </label>
          </div>

          <button
            onClick={handleSave} disabled={saving}
            className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition"
            data-testid="shows-form-save"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : editingId ? "Update Show" : "Create Show"}
          </button>
        </div>
      )}

      {/* Weekly Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 text-white/20 animate-spin" />
        </div>
      ) : shows.length === 0 ? (
        <div className="text-center py-16 bg-white/[0.02] border border-white/5 rounded-2xl" data-testid="shows-empty">
          <Mic2 className="w-14 h-14 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm mb-1">No shows scheduled yet</p>
          <p className="text-white/20 text-xs">Add your first show to display it on your station page</p>
        </div>
      ) : (
        <div className="bg-white/[0.02] border border-white/8 rounded-2xl overflow-hidden" data-testid="shows-grid">
          {DAYS.map((day, dayIdx) => {
            const dayShows = showsByDay[dayIdx] || [];
            if (dayShows.length === 0) return null;
            return (
              <div key={dayIdx} className="border-b border-white/5 last:border-0">
                <div className="px-5 py-3 bg-white/[0.03]">
                  <h4 className="text-white/60 text-xs font-bold uppercase tracking-widest">{day}</h4>
                </div>
                <div className="divide-y divide-white/[0.03]">
                  {dayShows
                    .sort((a, b) => a.start_hour * 60 + a.start_minute - (b.start_hour * 60 + b.start_minute))
                    .map((show, i) => {
                      const c = COLORS[i % COLORS.length];
                      return (
                        <div key={show.show_id} className={`flex items-center gap-4 px-5 py-3 ${c.bg} hover:bg-white/[0.04] transition group`}>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className={`w-2 h-8 rounded-full ${c.dot}`} />
                            {show.dj_image ? (
                              <img src={show.dj_image} alt={show.dj_name} className="w-8 h-8 rounded-full object-cover border border-white/10" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                                <User className="w-4 h-4 text-white/20" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium text-sm truncate">{show.show_name}</p>
                              {!show.is_active && <span className="text-white/20 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">Inactive</span>}
                            </div>
                            <p className="text-white/40 text-xs flex items-center gap-1.5 mt-0.5">
                              <Mic2 className="w-3 h-3" /> {show.dj_name}
                              {show.description && <span className="text-white/20 ml-2">— {show.description}</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 text-white/50 text-xs">
                            <Clock className="w-3 h-3" />
                            {formatHour(show.start_hour, show.start_minute)} – {formatHour(show.end_hour, show.end_minute)}
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                            <button onClick={() => startEdit(show)} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/10 transition" data-testid={`shows-edit-${show.show_id}`}>
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDelete(show.show_id)} className="p-1.5 text-white/30 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition" data-testid={`shows-delete-${show.show_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
